import { OnboardingStepFrame } from "@/components/onboarding/OnboardingStepFrame";
import {
  OnboardingStepProvider,
  OnboardingStepRenderer,
  type OnboardingStep,
} from "@/components/onboarding/OnboardingStepProvider";
import { MatterExpensesList } from "@/components/onboarding/steps/MatterExpensesStep";
import { MatterStatusesList } from "@/components/onboarding/steps/MatterStatusesStep";
import { IncidentTypesList } from "@/components/onboarding/steps/MatterTypesStep";
import { ProfilePhotosStep } from "@/components/onboarding/steps/ProfilePhotosStep";
import { TaskPlansStep } from "@/components/onboarding/steps/TaskPlansStep";
import { WelcomeStep } from "@/components/onboarding/steps/WelcomeStep";
import { UserProvider } from "@/components/UserProvider";
import { getCurrentUserOptions } from "@/lib/data/react-query";
import { redirectIfNotAuthenticated } from "@/lib/utils/auth";
import { createFileRoute, redirect } from "@tanstack/react-router";

export const Route = createFileRoute("/onboarding/")({
  component: RouteComponent,
  beforeLoad: async ({ context, location }) => {
    redirectIfNotAuthenticated({ session: context.session, location });
    const currentUserData = await context.queryClient.ensureQueryData(
      getCurrentUserOptions(),
    );

    if (currentUserData.data.firm.onboardingProgress === "COMPLETED") {
      throw redirect({ to: "/" });
    }
  },
});

const steps: OnboardingStep[] = [
  {
    id: "welcome",
    label: "Welcome",
    heading: "Welcome to LawWorks!",
    description:
      "Congrats on taking the first step in simplifying your law practice. We've got a few quick things to set up so you can get your firm up and running in the app.",
    nextLabel: "Get started",
    Component: WelcomeStep,
  },
  {
    id: "profile-photos",
    label: "Profile photos",
    heading: "Add your profile photo and firm logo",
    description:
      "Upload a profile photo and firm logo to personalize dashboards, invoices, and client communications. Both are optional.",
    Component: ProfilePhotosStep,
  },
  {
    id: "matter-status",
    label: "Matter statuses",
    heading: "Set up your matter statuses",
    description:
      "Matter statuses describe where a matter is in its lifecycle (for example, intake, active, closed). You can add new statuses, edit existing ones, or reorder them within each category to match your workflow. Click any existing status to edit it or create a new one.",
    Component: MatterStatusesList,
  },
  {
    id: "matter-types",
    label: "Incident types",
    heading: "Set up your incident types",
    description:
      "Incident types describe the type of matter (for example, personal injury, car accident, slip and fall). You can add new types, edit existing ones, or reorder them to match your workflow. Click any existing type to edit it or create a new one.",
    Component: IncidentTypesList,
  },
  {
    id: "matter-expenses",
    label: "Matter expenses",
    heading: "Set up your default matter expenses",
    description:
      "Default expenses help you prefill common costs (like filing fees or record retrieval) across matters. These will automatically apply to any matters you create. Add new expenses, edit existing ones, or remove entries you don't use.",
    Component: MatterExpensesList,
  },
  {
    id: "task-plans",
    label: "Task plans",
    heading: "Set up your task plans",
    description:
      "Task plans are reusable sets of tasks that automatically apply when something happens in a matter, like a status change. For example, a plan can include a “Start Litigation Prep” task that triggers when a matter moves to Litigation. Add new task plans, edit existing ones, or reorder them to match your workflow.",
    Component: TaskPlansStep,
  },
];

function RouteComponent() {
  return (
    <UserProvider>
      <OnboardingStepProvider steps={steps}>
        <OnboardingStepFrame>
          <OnboardingStepRenderer />
        </OnboardingStepFrame>
      </OnboardingStepProvider>
    </UserProvider>
  );
}
